import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import {
  DepartmentCodeSource,
  FacilityOrganizationCreate,
  FacilityOrganizationRead,
  FacilityOrganizationType,
} from "@/types/facilityOrganization/facilityOrganization";
import facilityOrganizationApi from "@/types/facilityOrganization/facilityOrganizationApi";

const STANDARD_DEPARTMENTS = [
  { key: "general_medicine", label: "General Medicine", code: "MED" },
  { key: "general_surgery", label: "General Surgery", code: "SUR" },
  { key: "orthopedics", label: "Orthopedics", code: "ORTHO" },
  { key: "gynecology", label: "Gynecology", code: "GYN" },
  { key: "obstetrics", label: "Obstetrics", code: "OBS" },
  { key: "pediatrics", label: "Pediatrics", code: "PED" },
  { key: "dermatology", label: "Dermatology", code: "DERM" },
  { key: "ent", label: "ENT", code: "ENT" },
  { key: "ophthalmology", label: "Ophthalmology", code: "OPH" },
  { key: "cardiology", label: "Cardiology", code: "CARD" },
  { key: "neurology", label: "Neurology", code: "NEURO" },
  { key: "neurosurgery", label: "Neurosurgery", code: "NS" },
  { key: "urology", label: "Urology", code: "URO" },
  { key: "gastroenterology", label: "Gastroenterology", code: "GASTRO" },
  {
    key: "pulmonology_chest",
    label: "Pulmonology / Chest",
    code: "CHEST",
  },
  { key: "endocrinology", label: "Endocrinology", code: "ENDO" },
  { key: "oncology", label: "Oncology", code: "ONCO" },
  { key: "psychiatry", label: "Psychiatry", code: "PSY" },
  { key: "dentistry", label: "Dentistry", code: "DENT" },
  { key: "physiotherapy", label: "Physiotherapy", code: "PHYSIO" },
  { key: "nephrology", label: "Nephrology", code: "NEPHRO" },
  { key: "rheumatology", label: "Rheumatology", code: "RHEUM" },
  { key: "hematology", label: "Hematology", code: "HEMATO" },
  {
    key: "infectious_diseases",
    label: "Infectious Diseases",
    code: "ID",
  },
  { key: "plastic_surgery", label: "Plastic Surgery", code: "PLASTIC" },
  { key: "vascular_surgery", label: "Vascular Surgery", code: "VASC" },
  { key: "thoracic_surgery", label: "Thoracic Surgery", code: "CTVS" },
  {
    key: "pediatric_surgery",
    label: "Pediatric Surgery",
    code: "PED-SURG",
  },
  { key: "family_medicine", label: "Family Medicine", code: "FM" },
  { key: "geriatrics", label: "Geriatrics", code: "GERI" },
  { key: "pain_management", label: "Pain Management", code: "PAIN" },
  { key: "rehabilitation", label: "Rehabilitation", code: "REHAB" },
  { key: "sports_medicine", label: "Sports Medicine", code: "SPORTS" },
  {
    key: "allergy_immunology",
    label: "Allergy & Immunology",
    code: "ALLERGY",
  },
  { key: "diabetology", label: "Diabetology", code: "DIAB" },
  { key: "andrology", label: "Andrology", code: "ANDRO" },
  { key: "fertility_ivf", label: "Fertility / IVF", code: "IVF" },
  { key: "cosmetology", label: "Cosmetology", code: "COSMETIC" },
  { key: "transplant", label: "Transplant", code: "TRANSPLANT" },
  {
    key: "intensive_care_unit",
    label: "Intensive Care Unit",
    code: "ICU",
  },
  {
    key: "coronary_care_unit",
    label: "Coronary Care Unit",
    code: "CCU",
  },
  { key: "neonatal_icu", label: "Neonatal ICU", code: "NICU" },
  { key: "pediatric_icu", label: "Pediatric ICU", code: "PICU" },
  { key: "trauma", label: "Trauma", code: "TRAUMA" },
  { key: "day_care", label: "Day Care", code: "DAY-CARE" },
  { key: "operation_theatre", label: "Operation Theatre", code: "OT" },
  { key: "laboratory", label: "Laboratory", code: "LAB" },
  { key: "radiology", label: "Radiology", code: "RAD" },
  { key: "pharmacy", label: "Pharmacy", code: "PHARM" },
  { key: "blood_bank", label: "Blood Bank", code: "BB" },
  { key: "dialysis", label: "Dialysis", code: "DIALYSIS" },
  { key: "ambulance", label: "Ambulance", code: "AMB" },
] as const;

const CUSTOM_DEPARTMENT_CODE_PATTERN = /^[A-Z]+$/;

function sanitizeDepartmentCode(value: string) {
  return value.toUpperCase().replace(/[^A-Z]/g, "");
}

interface Props {
  facilityId: string;
  parentId?: string;
  org?: FacilityOrganizationRead;

  trigger: React.ReactNode;

  tooltip?: string;
}

export default function FacilityOrganizationFormSheet({
  facilityId,
  parentId,
  org,
  trigger,
  tooltip,
}: Props) {
  const { t } = useTranslation();

  const isEditMode = !!org;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [duplicateCodeError, setDuplicateCodeError] = useState<string>("");
  const [hasCodeFieldBlurred, setHasCodeFieldBlurred] = useState(false);

  const formSchema = z
    .object({
      name: z.string().trim().max(100, t("max_100_characters")).default(""),
      description: z.string().trim().default(""),
      org_type: z.nativeEnum(FacilityOrganizationType).refine(
        (val) => {
          return (
            val === FacilityOrganizationType.DEPT ||
            val === FacilityOrganizationType.TEAM
          );
        },
        {
          message: t("invalid_organization_type"),
        },
      ),
      standard_department: z.string().optional(),
      custom_department_code: z
        .string()
        .trim()
        .max(10, t("max_10_characters"))
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (
        data.org_type === FacilityOrganizationType.TEAM &&
        !data.name.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["name"],
          message: t("field_required"),
        });
      }

      if (data.org_type === FacilityOrganizationType.DEPT) {
        if (!data.standard_department) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["standard_department"],
            message: t("field_required"),
          });
        }

        if (data.standard_department === "other") {
          if (!data.name.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["name"],
              message: t("field_required"),
            });
          }

          const customDepartmentCode =
            data.custom_department_code?.trim() ?? "";

          if (!customDepartmentCode) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["custom_department_code"],
              message: t("field_required"),
            });
          } else if (
            !CUSTOM_DEPARTMENT_CODE_PATTERN.test(customDepartmentCode)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["custom_department_code"],
              message: t("department_code_letters_only"),
            });
          } else if (customDepartmentCode.length > 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["custom_department_code"],
              message: t("max_10_characters"),
            });
          }
        }
      }
    });

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      org_type: FacilityOrganizationType.DEPT,
      standard_department: undefined,
      custom_department_code: "",
    },
  });

  const orgType = form.watch("org_type");
  const standardDepartment = form.watch("standard_department");
  const isDepartment = orgType === FacilityOrganizationType.DEPT;
  const isCustomDepartment = isDepartment && standardDepartment === "other";
  const selectedStandardDepartment = STANDARD_DEPARTMENTS.find(
    (department) => department.key === standardDepartment,
  );
  const namePlaceholder =
    orgType === FacilityOrganizationType.TEAM
      ? t("enter_team_name")
      : isCustomDepartment
        ? t("enter_department_name")
        : t("auto_filled_from_selected_department");
  const descriptionPlaceholder =
    orgType === FacilityOrganizationType.TEAM
      ? t("enter_team_description")
      : t("enter_department_description");

  const customDepartmentCodeValue = form.watch("custom_department_code");
  const nameValue = form.watch("name");

  useEffect(() => {
    if (isEditMode && org) {
      form.reset({
        name: org.name || "",
        description: org.description || "",
        org_type: org.org_type,
        standard_department:
          org.org_type === FacilityOrganizationType.DEPT
            ? org.department_code_source === DepartmentCodeSource.STANDARD &&
              org.standard_department
              ? org.standard_department
              : "other"
            : undefined,
        custom_department_code:
          org.org_type === FacilityOrganizationType.DEPT &&
          org.department_code_source === DepartmentCodeSource.CUSTOM
            ? org.department_code || ""
            : "",
      });
    }
    setHasCodeFieldBlurred(false);
  }, [form, isEditMode, org, open]);

  // Fetch all facility departments for duplicate code validation
  const { data: facilitiesDepartmentsResponse } = useQuery({
    queryKey: ["facilityOrganization", "list", facilityId],
    queryFn: query(facilityOrganizationApi.list, {
      pathParams: { facilityId },
      queryParams: {
        limit: 1000, // Fetch all departments
      },
    }),
    enabled: !!facilityId && isCustomDepartment && open,
  });

  // Validate duplicate department codes
  useEffect(() => {
    if (!isCustomDepartment || !customDepartmentCodeValue) {
      setDuplicateCodeError("");
      return;
    }

    const sanitizedCode = sanitizeDepartmentCode(customDepartmentCodeValue);

    // Check against standard departments
    const standardDept = STANDARD_DEPARTMENTS.find(
      (dept) => dept.code === sanitizedCode,
    );
    if (standardDept) {
      setDuplicateCodeError(
        t("department_code_exists_for", { departmentName: standardDept.label }),
      );
      return;
    }

    // Check against created facility departments (exclude current department in edit mode)
    const facilityDepartments = facilitiesDepartmentsResponse?.results || [];
    const existingDept = facilityDepartments.find(
      (dept) =>
        dept.department_code === sanitizedCode &&
        (!isEditMode || dept.id !== org?.id), // Don't flag as duplicate if it's the current department
    );

    if (existingDept) {
      setDuplicateCodeError(
        t("department_code_exists_for", { departmentName: existingDept.name }),
      );
    } else {
      setDuplicateCodeError("");
    }
  }, [
    customDepartmentCodeValue,
    isCustomDepartment,
    facilitiesDepartmentsResponse,
    isEditMode,
    org,
    t,
  ]);

  const { mutate: createOrganization, isPending: isCreating } = useMutation({
    mutationFn: mutate(facilityOrganizationApi.create, {
      pathParams: { facilityId },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["facilityOrganization"],
      });
      toast.success(t("organization_created_successfully"));
      setOpen(false);
      form.reset();
    },
  });

  const { mutate: updateOrganization, isPending: isUpdating } = useMutation({
    mutationFn: mutate(facilityOrganizationApi.update, {
      pathParams: { facilityId, organizationId: org?.id },
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["facilityOrganization"],
      });
      toast.success(t("organizations_updated_successfully"));
      setOpen(false);
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const data: FacilityOrganizationCreate = {
      facility: facilityId,
      name: "",
      description: values.description.trim(),
      org_type: values.org_type,
      parent: parentId,
      department_code_source: null,
      standard_department: null,
      custom_department_code: null,
    };

    if (values.org_type === FacilityOrganizationType.TEAM) {
      data.name = values.name.trim();
    } else if (values.standard_department === "other") {
      data.name = values.name.trim();
      data.department_code_source = DepartmentCodeSource.CUSTOM;
      data.custom_department_code = sanitizeDepartmentCode(
        values.custom_department_code?.trim() || "",
      );
      data.standard_department = null;
    } else {
      const selectedDepartment = STANDARD_DEPARTMENTS.find(
        (department) => department.key === values.standard_department,
      );
      if (!selectedDepartment) {
        return;
      }
      data.name = selectedDepartment.label;
      data.department_code_source = DepartmentCodeSource.STANDARD;
      data.standard_department = values.standard_department;
      data.custom_department_code = null;
    }

    if (isEditMode) {
      updateOrganization(data);
    } else {
      createOrganization(data);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <SheetTrigger asChild>{trigger}</SheetTrigger>
          </TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        <SheetTrigger asChild>{trigger}</SheetTrigger>
      )}
      <SheetContent onCloseAutoFocus={(event) => event.preventDefault()}>
        <SheetHeader>
          <SheetTitle>
            {isEditMode
              ? t("edit_department_team")
              : t("create_department_team")}
          </SheetTitle>
          <SheetDescription>
            {isEditMode
              ? t("edit_department_team_description")
              : t("create_department_team_description")}
          </SheetDescription>
        </SheetHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 py-4"
          >
            <FormField
              control={form.control}
              name="org_type"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>{t(`type`)}</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={(value) => {
                      field.onChange(value);
                      if (value === FacilityOrganizationType.TEAM) {
                        form.setValue("standard_department", undefined, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                        form.setValue("custom_department_code", "", {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                        form.setValue("name", "", {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                        form.clearErrors([
                          "name",
                          "standard_department",
                          "custom_department_code",
                        ]);
                      }
                    }}
                  >
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue
                          placeholder={t("select_organization_type")}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(FacilityOrganizationType)
                        .filter(
                          (type) => type !== FacilityOrganizationType.ROOT,
                        )
                        .map((type) => (
                          <SelectItem key={type} value={type}>
                            {type === FacilityOrganizationType.DEPT
                              ? t("department")
                              : t("team")}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isDepartment && (
              <FormField
                control={form.control}
                name="standard_department"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel aria-required>{t("department")}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        if (value !== "other") {
                          const selectedDepartment = STANDARD_DEPARTMENTS.find(
                            (department) => department.key === value,
                          );
                          form.setValue(
                            "name",
                            selectedDepartment?.label || "",
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            },
                          );
                          form.setValue(
                            "custom_department_code",
                            selectedDepartment?.code || "",
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            },
                          );
                        } else {
                          form.setValue("name", "", {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                          form.setValue("custom_department_code", "", {
                            shouldDirty: true,
                            shouldValidate: false,
                          });
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger ref={field.ref}>
                          <SelectValue placeholder={t("select_department")} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="other">{t("other")}</SelectItem>
                        {STANDARD_DEPARTMENTS.map((department) => (
                          <SelectItem
                            key={department.key}
                            value={department.key}
                          >
                            {department.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {(orgType === FacilityOrganizationType.TEAM || isDepartment) && (
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => {
                  const currentNameLength = nameValue?.length || 0;
                  return (
                    <FormItem className="space-y-2">
                      <div className="flex justify-between items-center">
                        <FormLabel aria-required>{t("name")}</FormLabel>
                        <span className="text-xs text-muted-foreground">
                          {currentNameLength}/100
                        </span>
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          value={
                            isDepartment && !isCustomDepartment
                              ? (selectedStandardDepartment?.label ?? "")
                              : (field.value ?? "")
                          }
                          disabled={isDepartment && !isCustomDepartment}
                          placeholder={namePlaceholder}
                          maxLength={100}
                          onChange={(event) => {
                            if (isDepartment && !isCustomDepartment) {
                              return;
                            }
                            if (event.target.value.length <= 100) {
                              field.onChange(event);
                            }
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            )}

            {isDepartment && (
              <FormField
                control={form.control}
                name="custom_department_code"
                render={({ field }) => {
                  const currentCodeLength =
                    customDepartmentCodeValue?.length || 0;
                  return (
                    <FormItem className="space-y-2">
                      <div className="flex justify-between items-center">
                        <FormLabel aria-required>{t("department_code")}</FormLabel>
                        <span className="text-xs text-muted-foreground">
                          {currentCodeLength}/10
                        </span>
                      </div>
                      <FormControl>
                        <Input
                          {...field}
                          value={
                            isCustomDepartment
                              ? (field.value ?? "")
                              : (selectedStandardDepartment?.code ?? "")
                          }
                          disabled={!isCustomDepartment}
                          placeholder={t("enter_custom_department_code")}
                          maxLength={10}
                          onChange={(event) => {
                            if (!isCustomDepartment) {
                              return;
                            }
                            const sanitized = sanitizeDepartmentCode(
                              event.target.value,
                            );
                            if (sanitized.length <= 10) {
                              field.onChange(sanitized);
                            }
                          }}
                          onBlur={() => setHasCodeFieldBlurred(true)}
                          autoCapitalize="characters"
                          autoCorrect="off"
                          spellCheck={false}
                        />
                      </FormControl>
                      <FormMessage />
                      {duplicateCodeError && hasCodeFieldBlurred && (
                        <p className="text-sm font-medium text-red-400">
                          {duplicateCodeError}
                        </p>
                      )}
                    </FormItem>
                  );
                }}
              />
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel>{t("description")}</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder={descriptionPlaceholder} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end mt-6 space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setOpen(false);
                  form.reset();
                  setDuplicateCodeError("");
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                type="submit"
                disabled={
                  isPending ||
                  !form.formState.isValid ||
                  !form.formState.isDirty ||
                  !!duplicateCodeError
                }
              >
                {isPending
                  ? isEditMode
                    ? t("updating")
                    : t("creating")
                  : isEditMode
                    ? t("update_organization")
                    : t("create_organization")}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
